import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Divider,
  IconButton,
  InputAdornment,
  CircularProgress,
  Container,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Casino,
  Email,
  Lock
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { affiliateService } from '../../services/affiliateService';
import BackButton from '../BackButton';

function LoginScreen() {
  const [isLogin, setIsLogin] = useState(() => new URLSearchParams(window.location.search).get('signup') !== 'true');
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [referralCodeValidation, setReferralCodeValidation] = useState({ 
    isValidating: false, 
    isValid: null, 
    message: '' 
  });
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    referralCode: '',
  });

  const { login, signup, resetPassword } = useAuth();

  // Check for stored referral code on component mount
  useEffect(() => {
    const storedReferralCode = localStorage.getItem('referralCode');
    if (storedReferralCode && formData.referralCode === '') {
      setFormData(prev => ({
        ...prev,
        referralCode: storedReferralCode
      }));
      // Validate the pre-filled referral code
      validateReferralCode(storedReferralCode);
      // Clear it from localStorage after using it
      localStorage.removeItem('referralCode');
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    
    // Handle referral code validation
    if (name === 'referralCode') {
      if (value.trim() === '') {
        setReferralCodeValidation({ isValidating: false, isValid: null, message: '' });
      } else {
        validateReferralCode(value);
      }
    }
  };

  // Validate referral code with debouncing
  const validateReferralCode = async (code) => {
    if (!code || code.trim() === '') {
      setReferralCodeValidation({ isValidating: false, isValid: null, message: '' });
      return;
    }

    setReferralCodeValidation({ isValidating: true, isValid: null, message: 'Validating...' });

    try {
      const validation = await affiliateService.validateReferralCode(code);
      if (validation.isValid) {
        setReferralCodeValidation({
          isValidating: false,
          isValid: true,
          message: `Valid! Referred by ${validation.referrer.displayName}`
        });
      } else {
        setReferralCodeValidation({
          isValidating: false,
          isValid: false,
          message: validation.error
        });
      }
    } catch (error) {
      setReferralCodeValidation({
        isValidating: false,
        isValid: false,
        message: 'Unable to validate referral code'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (showResetPassword) {
        // Password reset
        if (!formData.email) {
          setError('Please enter your email address');
          setLoading(false);
          return;
        }
        await resetPassword(formData.email);
        setMessage('Check your email for password reset instructions');
        setShowResetPassword(false);
      } else if (isLogin) {
        // Login
        if (!formData.email || !formData.password) {
          setError('Please fill in all fields');
          setLoading(false);
          return;
        }
        await login(formData.email, formData.password);
      } else {
        // Sign up
        if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
          setError('Please fill in all fields');
          setLoading(false);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        // Check if referral code is provided but invalid
        if (formData.referralCode && formData.referralCode.trim() !== '' && referralCodeValidation.isValid === false) {
          setError('Please enter a valid referral code or leave it blank');
          setLoading(false);
          return;
        }
        await signup(formData.email, formData.password, formData.firstName, formData.lastName, formData.referralCode);
      }
    } catch (err) {
      console.error('Authentication error:', err);
      // Handle specific Firebase auth errors
      switch (err.code) {
        case 'auth/user-not-found':
          setError('No account found with this email');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password');
          break;
        case 'auth/email-already-in-use':
          setError('An account with this email already exists');
          break;
        case 'auth/weak-password':
          setError('Password is too weak');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later');
          break;
        default:
          setError('Authentication failed. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setShowResetPassword(false);
    setError('');
    setMessage('');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      referralCode: '',
    });
  };

  const toggleResetPassword = () => {
    setShowResetPassword(!showResetPassword);
    setIsLogin(true);
    setError('');
    setMessage('');
  };

  return (
    <Container maxWidth="sm">
      <BackButton />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3
        }}
      >
        <Card 
          sx={{ 
            width: '100%',
            maxWidth: 400,
            boxShadow: { xs: 1, sm: 3 },
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Casino 
                sx={{ 
                  fontSize: 48, 
                  color: 'primary.main', 
                  mb: 1 
                }} 
              />
              <Typography variant="h4" component="h1" gutterBottom>
                Bettor Advantage
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {showResetPassword 
                  ? 'Reset your password' 
                  : isLogin 
                    ? 'Welcome back! Sign in to your account' 
                    : 'Create your account to get started'
                }
              </Typography>
            </Box>

            {/* Error/Success Messages */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {message && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {message}
              </Alert>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit}>
              {/* First Name (Sign up only) */}
              {!isLogin && !showResetPassword && (
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  margin="normal"
                  autoComplete="given-name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ width: 24 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {/* Last Name (Sign up only) */}
              {!isLogin && !showResetPassword && (
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  margin="normal"
                  autoComplete="family-name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ width: 24 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {/* Email */}
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                margin="normal"
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password (not for reset) */}
              {!showResetPassword && (
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  margin="normal"
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {/* Confirm Password (Sign up only) */}
              {!isLogin && !showResetPassword && (
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  margin="normal"
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {/* Referral Code (Sign up only) */}
              {!isLogin && !showResetPassword && (
                <TextField
                  fullWidth
                  label="Referral Code (Optional)"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleInputChange}
                  margin="normal"
                  placeholder="Enter a friend's referral code"
                  helperText={referralCodeValidation.message}
                  error={referralCodeValidation.isValid === false}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Casino color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: referralCodeValidation.isValidating ? (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ) : referralCodeValidation.isValid === true ? (
                      <InputAdornment position="end">
                        <Box sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
                          ✓
                        </Box>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    '& .MuiFormHelperText-root': {
                      color: referralCodeValidation.isValid === true ? 'success.main' : 
                             referralCodeValidation.isValid === false ? 'error.main' : 'text.secondary'
                    }
                  }}
                />
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : showResetPassword ? (
                  'Send Reset Email'
                ) : isLogin ? (
                  'Sign In'
                ) : (
                  'Create Account'
                )}
              </Button>

              {/* Forgot Password Link (Login only) */}
              {isLogin && !showResetPassword && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={toggleResetPassword}
                    sx={{ textDecoration: 'none' }}
                  >
                    Forgot your password?
                  </Link>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Toggle between Login/Signup */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {showResetPassword ? (
                    <>
                      Remember your password?{' '}
                      <Link
                        component="button"
                        type="button"
                        onClick={toggleResetPassword}
                        sx={{ fontWeight: 'medium' }}
                      >
                        Sign In
                      </Link>
                    </>
                  ) : isLogin ? (
                    <>
                      Don't have an account?{' '}
                      <Link
                        component="button"
                        type="button"
                        onClick={toggleMode}
                        sx={{ fontWeight: 'medium' }}
                      >
                        Sign Up
                      </Link>
                    </>
                  ) : (
                    <>
                      Already have an account?{' '}
                      <Link
                        component="button"
                        type="button"
                        onClick={toggleMode}
                        sx={{ fontWeight: 'medium' }}
                      >
                        Sign In
                      </Link>
                    </>
                  )}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}

export default LoginScreen;
